import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";

import { Box, Paper, Typography, Divider, TextField, MenuItem } from '@mui/material';
import { ArtworkProvenance } from "../../models/ArtworkProvenance";
import { useReferenceData } from '../../components/ReferenceDataContext';
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useUser } from '../../components/UserContext';

import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../styles/mui_theme";

export type ProvenanceMode = "view" | "edit";

export type ProvenanceFormHandle = {
    getValues: () => ArtworkProvenance;
    setValues: (values: ArtworkProvenance) => void;
    setMode: (mode: ProvenanceMode) => void;
    reset: () => void;
};

type ProvenanceFormProps = {
    initialValues?: ArtworkProvenance;
    mode?: ProvenanceMode;                    // default "view"
    onChange?: (values: ArtworkProvenance) => void;
    onSubmit?: (values: ArtworkProvenance) => void;
    onCancel: () => void;
    showHeaderActions?: boolean;              // if true, show Edit/Save/Cancel buttons in header
    title?: string;
};

const defaultValues: ArtworkProvenance = {
    dateAcquired: "",
    sourceId: 0,
    sourceName: "",
    provenance: "",
    notes: "",
};

export const ProvenanceForm = forwardRef<ProvenanceFormHandle, ProvenanceFormProps>(
    (
        {
            initialValues = defaultValues,
            mode = "view",
            onChange,
            onSubmit,
            onCancel,
            showHeaderActions = true,
            title = "Provenance",
        },
        ref
    ) => {
        const [formMode, setFormMode] = useState<ProvenanceMode>(mode);
        const [values, setValues] = useState<ArtworkProvenance>(initialValues);
        const [originalValues, setOriginalValues] = useState<ArtworkProvenance>(initialValues);

        const { sources } = useReferenceData();


        const { isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
        const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
        const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

        useEffect(() => {
            setValues(initialValues);
            setOriginalValues(initialValues);

        }, [initialValues]);

        useEffect(() => {
            setFormMode(mode);
        }, [mode]);

        useImperativeHandle(ref, () => ({
            getValues: () => values,
            setValues: (newValues: ArtworkProvenance) => {
                setValues(newValues);
                onChange?.(newValues);
            },
            setMode: (newMode: ProvenanceMode) => setFormMode(newMode),
            reset: () => {
                setValues(originalValues);
                setFormMode("view");
                onCancel();
            },
        }), [values]);

        const handleFieldChange = (field: keyof ArtworkProvenance, value: string) => {

            const newValues = { ...values, [field]: value };

            setValues(newValues);
            onChange?.(newValues);
        };

        const handleSaveClick = () => {
            onSubmit?.(values);
            setOriginalValues(values);
            setFormMode("view");
        };

        const handleCancelClick = () => {
            setValues(originalValues);
            setFormMode("view");
            onCancel();
        };

        const isView = formMode === "view";

        return (
            <ThemeProvider theme={theme}>
            <Paper elevation={3} square={false} sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
                <div className="section-header">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Artwork Provenance</Typography>
                    {isView && (
                        <button className="responsive-button" onClick={() => setFormMode("edit")} hidden={true}>Edit</button>
                    )}
                    </Box>
                </div>
                <Divider sx={{ my: 2 }} />

                <section className="bg-white rounded-lg shadow p-4 space-y-4">

                    {!isView ? (
                        <>
                            {/* Acquisition Date & Source */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <DatePicker
                                        label="Acquired Date"
                                        value={dayjs(values.dateAcquired, "DD-MM-YYYY")}
                                        onChange={(e) => handleFieldChange("dateAcquired", e?.format("DD-MM-YYYY") ?? '')}
                                        className="required-field"
                                    />
                                </div>

                                <div>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Acquired From Type"
                                        size="small"
                                        margin="normal"
                                        value={values.sourceId}
                                        onChange={(e) => handleFieldChange("sourceId", e.target.value)}
                                    >
                                        {sources?.map((source: any) => (
                                            <MenuItem key={source.id} value={source.id}>
                                                {source.type}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </div>
                            </div>

                            {/* Source Name */}
                            <div>
                                <TextField
                                    fullWidth
                                    label="Acquired From Details"
                                    size="small"
                                    margin="normal"
                                    multiline
                                    rows={1}
                                    value={values.sourceName}
                                    onChange={(e) => handleFieldChange("sourceName", e.target.value)}
                                />
                            </div>

                            {/* Provenance Details */}
                            <div>
                                <TextField
                                    fullWidth
                                    label="Provenance Details"
                                    size="small"
                                    margin="normal"
                                    multiline
                                    rows={3}
                                    value={values.provenance}
                                    onChange={(e) => handleFieldChange("provenance", e.target.value)}
                                />
                            </div>

                            {/* Notes */}
                            <div>
                                <TextField
                                    fullWidth
                                    label="Notes"
                                    size="small"
                                    margin="normal"
                                    multiline
                                    rows={1}
                                    value={values.notes}
                                    onChange={(e) => handleFieldChange("notes", e.target.value)}
                                />
                            </div>

                            {/* Buttons */}
                            {showHeaderActions && (
                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        className="px-3 py-1 border rounded hover:bg-gray-50"
                                        onClick={handleCancelClick}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                                        onClick={handleSaveClick}
                                    >
                                        Save
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        // Read-only summary view
                        <div className="space-y-4 text-sm">
                            <div className="pb-4">
                                <div className="text-xs font-medium text-slate-700 mb-0.5">
                                    Acquisition Date
                                </div>
                                    <div>{values.dateAcquired || "—"}</div>
                            </div>

                            <div className="pb-4">
                                <div className="text-xs font-medium text-slate-700 mb-0.5">Source</div>
                                <div>{values.sourceName || "—"}</div>
                            </div>

                            <div className="pb-4">
                                <div className="text-xs font-medium text-slate-700 mb-0.5">
                                    Provenance Details
                                </div>
                                <div className="whitespace-pre-wrap">
                                    {values.provenance || "—"}
                                </div>
                            </div>

                            <div className="pb-2">
                                <div className="text-xs font-medium text-slate-700 mb-0.5">Notes</div>
                                <div className="whitespace-pre-wrap">
                                    {values.notes || "—"}
                                </div>
                            </div>
                        </div>
                    )}
                </section>
                </Paper>
            </ThemeProvider>
        );
    }
);
